import * as THREE from "three";
import { Cell, CellKind, LevelPhase, LevelSim, Occupant } from "@highmarch/domain";
import { Butterflies, Meadow, PollenMotes } from "./meadow";
import { GroundMesh } from "../groundMesh";
import { HqMaterials } from "../hqMaterials";
import { CELL_JITTER, CRITTER_SCALE, CRITTER_SHADOW_ALPHA, CritterKind, CritterWorld, FLEE_RANGE, critterGround } from "./critterTuning";
import { Critter } from "./critter";
import { Flock, softDisc } from "./critterFlock";

export { CRITTER_SHADOW_ALPHA, Critter, critterGround, softDisc, type CritterKind, type CritterWorld };

/**
 * Ambient wildlife (presentation only, no sim state): a handful of rabbits and birds live on the road and its verges.
 * They nibble, sit up to look round, hop or flutter to a nearby spot, and bolt when a wave starts or an enemy comes
 * within reach — rabbits bound away from the threat and vanish, birds take off. Once the map is calm again new ones
 * wander in after a pause. Built from primitives so the set needs no art drop; colours follow the HQ palette.
 *
 * Their feet are on the ground as drawn (GroundMesh.drawnAt, the baked triangles), re-sampled every frame, with
 * a soft contact shadow under each; they get about in short squash-and-stretch bounds (critterHop.ts).
 */
export class CritterViews implements CritterWorld {
  readonly group = new THREE.Group();
  private readonly critters: Critter[] = [];
  private calmFor = 0;
  private spawnIn = 0.6;
  static readonly MaxAlive = 6;
  static readonly FleeRange = FLEE_RANGE;
  /** Scale over the modelled size, and the spread of a critter's spot inside its cell (critterTuning.ts). */
  static readonly Scale = CRITTER_SCALE;
  static readonly CellJitter = CELL_JITTER;
  /** Cells round the enemy portal a critter keeps out of (Chebyshev distance). */
  static readonly PortalClear = 1;

  /** The contact shadows' shared soft disc and quad. */
  private readonly shadowMat: THREE.MeshBasicMaterial;
  private readonly shadowGeo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);

  /** Birds circling high over the map, whose shadows slide across the ground below them. */
  private readonly flock: Flock;

  /** Pollen in the sunlight (null when the tier or reduced effects rule it out) and butterflies over the verges. */
  private readonly pollen: PollenMotes | null;
  private readonly butterflies: Butterflies;
  static readonly ButterflyCount = 5;

  constructor(private readonly sim: LevelSim) {
    this.flock = new Flock((sim.grid.width - 1) / 2, (sim.grid.height - 1) / 2, Math.min(sim.grid.width, sim.grid.height) * 0.32);
    this.group.add(this.flock.group);
    const motes = Meadow.pollenCount(Meadow.tier(), Meadow.reduced());
    this.pollen = motes > 0 ? new PollenMotes(motes, GroundMesh.WindDir) : null;
    if (this.pollen) this.group.add(this.pollen.points);
    this.butterflies = new Butterflies(Meadow.reduced() ? 2 : CritterViews.ButterflyCount, () => this.flutterSpot());
    this.group.add(this.butterflies.mesh);
    this.shadowMat = new THREE.MeshBasicMaterial({ map: softDisc(), transparent: true, opacity: CRITTER_SHADOW_ALPHA, depthWrite: false, toneMapped: false });
  }

  /** Somewhere over the grass beside the road for a butterfly to make for (anywhere on the grass early in a level). */
  private flutterSpot(): THREE.Vector3 | null {
    let c = this.pickCell();
    const g = this.sim.grid;
    for (let tries = 0; !c && tries < 12; tries++) {
      const r = new Cell(Math.floor(Math.random() * g.width), Math.floor(Math.random() * g.height));
      if (this.grassCell(r)) c = r;
    }
    if (!c) return null;
    return new THREE.Vector3(c.x + (Math.random() - 0.5) * 0.8, 0, c.y + (Math.random() - 0.5) * 0.8);
  }

  private threatened(): boolean {
    const s = this.sim;
    return s.phase === LevelPhase.WaveRunning || s.aliveEnemies > 0;
  }

  private readonly enemies: THREE.Vector3[] = [];
  private readonly enemyPos: THREE.Vector3[] = [];

  update(dt: number): void {
    const s = this.sim;
    const threatened = this.threatened();
    this.calmFor = threatened ? 0 : this.calmFor + dt;
    // enemy positions for the local flee check
    // Only gathered when there is a critter to warn. In a battle they have all fled, and this ran over every enemy
    // each frame for nothing; the vectors are kept between frames rather than made anew.
    const enemies = this.enemies;
    enemies.length = 0;
    if (this.critters.length > 0) {
      let n = 0;
      for (const e of s.enemySlots) {
        if (!e.alive) continue;
        const v = this.enemyPos[n] ?? (this.enemyPos[n] = new THREE.Vector3());
        enemies.push(v.set(e.x.toNumber(), 0, e.y.toNumber()));
        n++;
      }
    }
    for (const c of this.critters) c.update(dt, enemies, threatened, this);
    this.flock.update(dt, this.group);
    this.pollen?.update(dt, HqMaterials.time.value);
    this.butterflies.update(dt, threatened);
    for (let i = this.critters.length - 1; i >= 0; i--) if (this.critters[i].gone) { this.group.remove(this.critters[i].root, this.critters[i].shadow); this.critters[i].dispose(); this.critters.splice(i, 1); }
    // repopulate while things are calm
    if (!threatened && this.calmFor > 1.2 && this.critters.length < CritterViews.MaxAlive) {
      this.spawnIn -= dt;
      if (this.spawnIn <= 0) { this.spawnIn = 1.5 + Math.random() * 3; this.spawnOne(); }
    }
  }

  /**
   * A cell on the road or right beside it, off the fortress, blockers, towers, the portal and other critters. The
   * road and its verge are where the grass scatter is thin: a critter out in the field is half hidden by the tufts.
   */
  private pickCell(): Cell | null {
    const s = this.sim, road = s.road.roadCells;
    if (road.length === 0) return null;
    for (let tries = 0; tries < 24; tries++) {
      // on or right beside the road first; a cell further out only when nothing near will do (a road still to draft)
      const reach = tries < 16 ? 2.4 : 4.4;
      const r = road[Math.floor(Math.random() * road.length)];
      const c = new Cell(r.x + Math.round((Math.random() - 0.5) * reach), r.y + Math.round((Math.random() - 0.5) * reach));
      if (!this.roamCell(c)) continue;
      if (this.critters.some((k) => k.cell.equals(c))) continue;
      return c;
    }
    return null;
  }

  /** Somewhere next to `from` to go to, on the road or its verge, with the point on the drawn ground; null if boxed in. */
  nextSpot(from: Cell): { cell: Cell; at: THREE.Vector3 } | null {
    for (let k = 0; k < 8; k++) {
      const c = new Cell(from.x + Math.round((Math.random() - 0.5) * 2.4), from.y + Math.round((Math.random() - 0.5) * 2.4));
      if (c.equals(from) || !this.roamCell(c) || this.critters.some((k) => k.cell.equals(c))) continue;   // one to a cell, or they pile up
      const x = c.x + (Math.random() - 0.5) * CritterViews.CellJitter, z = c.y + (Math.random() - 0.5) * CritterViews.CellJitter;
      return { cell: c, at: new THREE.Vector3(x, critterGround(x, z), z) };
    }
    return null;
  }

  /**
   * Where a critter may be: a grass cell beside the road, or a road cell away from the portal — and never against the
   * fortress, whose walls overhang its footprint (a rabbit there showed up sitting on the battlements).
   */
  roamCell(c: Cell): boolean {
    if (this.nearFortress(c)) return false;
    if (this.grassCell(c)) return this.nearRoad(c);
    const s = this.sim, g = s.grid;
    if (!g.inBounds(c) || g.kindAt(c) !== CellKind.Empty || s.road.occupantAt(c) !== Occupant.None) return false;
    if (!s.road.roadCells.some((r) => r.equals(c))) return false;
    const portal = s.road.portal;
    return Math.max(Math.abs(c.x - portal.x), Math.abs(c.y - portal.y)) > CritterViews.PortalClear;
  }

  /** True within `RoamFromRoad` cells (Chebyshev) of a road cell. */
  private nearRoad(c: Cell): boolean {
    return this.sim.road.roadCells.some((r) => Math.max(Math.abs(c.x - r.x), Math.abs(c.y - r.y)) <= CritterViews.RoamFromRoad);
  }
  /**
   * How far from the road a critter may wander, cells. Two, so there is grass to live on before the road is drafted
   * (it is only the gate then, and the fortress keeps them off the cells round it); `pickCell` still favours one.
   */
  static readonly RoamFromRoad = 2;

  /** True within `FortressClear` cells (Chebyshev) of the fortress footprint. */
  private nearFortress(c: Cell): boolean {
    const g = this.sim.grid, r = CritterViews.FortressClear;
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      const n = new Cell(c.x + dx, c.y + dy);
      if (g.inBounds(n) && g.kindAt(n) === CellKind.Base) return true;
    }
    return false;
  }
  /** Cells round the fortress footprint a critter keeps out of. */
  static readonly FortressClear = 1;

  grassCell(c: Cell): boolean {
    const s = this.sim, g = s.grid;
    if (!g.inBounds(c) || g.kindAt(c) !== CellKind.Empty) return false;
    if (s.road.occupantAt(c) !== Occupant.None) return false;
    return !s.road.roadCells.some((r) => r.equals(c));
  }

  private spawnOne(): void {
    const cell = this.pickCell();
    if (!cell) return;
    const kind: CritterKind = Math.random() < 0.65 ? "rabbit" : "bird";
    const c = new Critter(kind, cell, this.shadowMat, this.shadowGeo);
    this.critters.push(c);
    this.group.add(c.root, c.shadow);
  }

  dispose(): void {
    for (const c of this.critters) { this.group.remove(c.root, c.shadow); c.dispose(); }
    this.critters.length = 0;
    this.shadowMat.map?.dispose(); this.shadowMat.dispose(); this.shadowGeo.dispose();
    this.flock.dispose(); this.pollen?.dispose(); this.butterflies.dispose(); Meadow.vfx = null;
  }
}
